import { IAbonementModel, IBaseValueModel, IParkingModel, IZoneModel } from '@pkp-g/core';
import { LocalizationModel } from '@shared/core';

export interface ITransactionDetailsResponse {
  view: ITransactionDetailsView;
}

export interface ITransactionDetailsView {
  event: ITransactionDetailsEvent;
  session: ITransactionSession;
  abonementGrant: ITransactionDetailsAbonementGrant;
  vehicle: string;
  document: ITransactionDetailsDocument;
  user: ITransactionDetailsUser;
  phone: string;
  vrp: string;
  zoneNumber: string;
  paymentType: TransactionPaymentType;
  time: string;
  sum: string;
}

export interface ITransactionDetailsEvent {
  uuid: string;
  kind: TransactionKind;
  date: string;
  accountsPaidRoadsideData?: ITransactionRoadsideEvent;
  accountsBuyAbonementData?: ITransactionAbonementEvent;
  accountsUnholdRoadsideData?: ITransactionUnholdEvent;
  userUuid: string;
  operationId: string;
}

export interface ITransactionRoadsideEvent {
  amount: string;
  sign: string;
  refillMethod: string;
  sessionUuid: string;
  confirmingDocument: string;
}

export interface ITransactionAbonementEvent {
  amount: string;
  vehicleVrp: string;
  grantAbonementUuid: string;
}

export interface ITransactionUnholdEvent {
  charged: string;
  unhold: string;
  sessionUuid: string;
}

export enum TransactionKind {
  KIND_EVENT_ACCOUNTS_HOLD_ROADSIDE = 'KIND_EVENT_ACCOUNTS_HOLD_ROADSIDE',
  KIND_EVENT_ACCOUNTS_BUY_ABONEMENT = 'KIND_EVENT_ACCOUNTS_BUY_ABONEMENT',
  KIND_EVENT_PAYMENTS_ACCOUNT_REFILL = 'KIND_EVENT_PAYMENTS_ACCOUNT_REFILL',
  KIND_EVENT_ACCOUNTS_CHARGED_ROADSIDE = 'KIND_EVENT_ACCOUNTS_CHARGED_ROADSIDE',
}

export const TransactionKindLabels: Record<TransactionKind, string> = {
  [TransactionKind.KIND_EVENT_ACCOUNTS_BUY_ABONEMENT]: 'Покупка абонемента',
  [TransactionKind.KIND_EVENT_ACCOUNTS_HOLD_ROADSIDE]: 'Оплата парковки',
  [TransactionKind.KIND_EVENT_ACCOUNTS_CHARGED_ROADSIDE]: 'Прерывание парковки',
  [TransactionKind.KIND_EVENT_PAYMENTS_ACCOUNT_REFILL]: 'Пополнение счета',
};

export interface ITransactionSession {
  end: number;
  parking: IParkingModel;
  parking_number: string;
  parking_uuid: string;
  session: ITransactionSessionInner;
  start: number;
  status: string;
  tariff: ITransactionDetailsTariff;
  user_uuid: string;
  uuid: string;
  zone: IZoneModel;
  zone_number: string;
}

export interface ITransactionSessionInner {
  company_uuid: string;
  created_at: string;
  end_price: string;
  parking_uuid: string;
  parking_zone_uuid: string;
  start_price: string;
  start_time: string;
  status: string;
  stop_time: string;
  tariff_uuid: string;
  updated_at: string;
  user_uuid: string;
  uuid: string;
  vehicle_type: string;
  vehicle_uuid: string;
  vehicle_vrp: string;
}

export interface ITransactionDetailsUser {
  account_id: string;
  birthdate: string;
  created_at: string;
  driver_license: string;
  email: string;
  email_confirm_token: string;
  email_to_confirm: string;
  first_name: string;
  is_email_confirmed: boolean;
  is_employee: boolean;
  last_activity_date: string;
  last_name: string;
  middle_name: string;
  phone: string;
  roles_uuids: unknown;
  status: number;
  username: string;
  uuid: IBaseValueModel;
  vehicles: unknown;
}

export interface ITransactionDetailsAbonementGrant {
  abonement: IAbonementModel;
  abonement_uuid: IBaseValueModel;
  created_at: string;
  end: string;
  operation_sign: string;
  paid_amount: number;
  start: string;
  uuid: IBaseValueModel;
  vrp: string;
}

export interface ITransactionDetailsDocument {
  operation_sign: string;
  operation_sum: number;
  raw_event_data: {
    operation_sign: string;
    operation_sum: number;
    refill_method: string;
  };
  refill_method: string;
}

export interface ITransactionDetailsTariff {
  city: string;
  created_at: string;
  description: LocalizationModel;
  name: LocalizationModel;
  periods: {
    day_offs: ITransactionPeriod[];
    holidays: ITransactionPeriod[];
    weekdays: ITransactionPeriod[];
  };
  rules: ITransactionRule[];
  updated_at: string;
  uuid: IBaseValueModel;
}

export interface ITransactionPeriod {
  end: number;
  rule_group_index: number;
  start: number;
}

export interface ITransactionRule {
  end: number;
  price: number;
  rule_group_index: number;
  start: number;
  vehicle_type: string;
}

export enum TransactionPaymentType {
  kaspiypay = 'kaspiypay',
  halykepay = 'halykepay',
  almaty_portal = 'almaty_portal',
  center_credit = 'center_credit',
  qiwi = 'qiwi',
  rbk_bank = 'rbk_bank',
  jusan_bank = 'jusan_bank',
  freedom_bank = 'freedom_bank',
  forte_bank = 'forte_bank',
  kassa_24 = 'kassa_24',
  kolesa_kz = 'kolesa_kz',
  bank_card = 'bank_card',
  external = 'external',
  stripe = 'stripe',
}

export const TransactionPaymentLabels: Record<TransactionPaymentType, string> = {
  [TransactionPaymentType.kaspiypay]: 'Kaspi',
  [TransactionPaymentType.halykepay]: 'Halyk',
  [TransactionPaymentType.almaty_portal]: 'Almaty Portal',
  [TransactionPaymentType.center_credit]: 'CenterCredit',
  [TransactionPaymentType.qiwi]: 'QIWI',
  [TransactionPaymentType.rbk_bank]: 'RBK Bank',
  [TransactionPaymentType.jusan_bank]: 'Jusan Bank',
  [TransactionPaymentType.freedom_bank]: 'Freedom Bank',
  [TransactionPaymentType.forte_bank]: 'Forte Bank',
  [TransactionPaymentType.kassa_24]: 'Kassa24',
  [TransactionPaymentType.kolesa_kz]: 'Kolesa.kz',
  [TransactionPaymentType.bank_card]: 'Банковская карта',
  [TransactionPaymentType.external]: 'Внешний источник',
  [TransactionPaymentType.stripe]: 'Банковская карта',
};
